/**
 * Step bookkeeping for the local agents (M4 spec §10.2, plan I4): resolves `NodeRef` ids to positions (the
 * manifest's stations, the M3 layout's fields) and reports each step's end to the strategic core once:
 * `localStepComplete` when a step's time ends (contacts and docked/mining/holding agents) or a real ship arrived
 * (`markArrived`), and `localReleased` for a leg that leaves the system (`exit` purpose), after which the agent is
 * removed from the store.
 *
 * `reportSteps` runs per tick and allocates nothing unless a report is due (message rate: the command object).
 */
import type { StrategicCommand } from '../strategic/types'
import type { NodeResolver } from './contacts'
import { PURPOSE, STEP_KIND, timeFraction, type ContactStore } from './contacts'

/** The station and field positions of the current system (float64 world metres). */
export interface NodeTable {
  readonly pos: Map<string, [number, number, number]>
}

export function createNodeTable(): NodeTable {
  return { pos: new Map() }
}

/** Resets the table to `stations` (manifest `StationView.pos`) and `fields` (layout `FieldSite.center`). */
export function fillNodeTable(
  t: NodeTable,
  stations: readonly { id: string; pos: readonly [number, number, number] }[],
  fields: readonly { id: string; center: readonly [number, number, number] }[],
): void {
  t.pos.clear()
  for (let i = 0; i < stations.length; i++) t.pos.set(stations[i].id, [stations[i].pos[0], stations[i].pos[1], stations[i].pos[2]])
  for (let i = 0; i < fields.length; i++) t.pos.set(fields[i].id, [fields[i].center[0], fields[i].center[1], fields[i].center[2]])
}

/** A `NodeResolver` over the table (bound once). */
export function nodeResolver(t: NodeTable): NodeResolver {
  return (id, out) => {
    const p = t.pos.get(id)
    if (p === undefined) return false
    out.x = p[0]
    out.y = p[1]
    out.z = p[2]
    return true
  }
}

/** Where the reports go (the strategic client's `command`). */
export type StepCommandSink = (cmd: StrategicCommand) => void

/** Marks `slot`'s step as arrived (a real ship reached a non-station target, or docked): reported next tick. */
export function markArrived(s: ContactStore, slot: number): void {
  if (s.reported[slot] === 0) s.progress[slot] = 1
  s.t1[slot] = s.t0[slot]
}

/**
 * Reports every step whose time ended at Reach time `s.time[0]`, once (`reported`). Exit legs are released and their
 * agents removed (`onRemove(slot)` runs first so the caller despawns a real ship). Returns the number of reports.
 */
export function reportSteps(s: ContactStore, send: StepCommandSink, onRemove: (slot: number) => void): number {
  const now = s.time[0]
  let n = 0
  for (let i = s.count - 1; i >= 0; i--) {
    if (s.reported[i] === 1) continue
    if (timeFraction(s, i, now) < 1) continue
    // A real ship still flying its leg reports when it arrives (the pilot marks it), not on the clock.
    if (s.eid[i] >= 0 && s.kind[i] === STEP_KIND.leg && s.t1[i] !== s.t0[i]) continue
    s.reported[i] = 1
    n++
    if (s.kind[i] === STEP_KIND.leg && s.purpose[i] === PURPOSE.exit) {
      send({ type: 'localReleased', agentId: s.ids[i] })
      onRemove(i)
    } else {
      send({ type: 'localStepComplete', agentId: s.ids[i] })
    }
  }
  return n
}
